import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { from, isObservable, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { AUTH_PROTECTED_RESOURCE_URLS } from '../../auth';
import type { AuthenticationSession } from '../../auth/auth-core/models/auth-session.model';
import { AuthSessionStatus } from '../../auth/auth-core/models/auth-session.model';
import { AuthStateService } from '../../auth/auth-core/services/auth-state.service';
import { AUTHENTICATION_ENABLED } from '../../auth/auth-core/tokens/auth-runtime.token';
import { FHIR_API_BASE_URL } from '../../http/api-client.tokens';
import { AUTH_TOKEN_PROVIDER } from '../../http/auth-token.provider';
import { ImpersonationService } from '../../services/impersonation.service';
import { IMPERSONATION_SESSION_HEADER, isHeaderSkippedByPolicy } from './header-injection.context';

function isFhirRequest(url: string, baseUrl: string | null | undefined): boolean {
  if (!baseUrl) {
    return false;
  }

  const normalizedBase = baseUrl.replace(/\/+$/, '');
  const pathPart = url.replace(/^https?:\/\/[^/]+/, '') || url;

  return (
    pathPart === normalizedBase ||
    pathPart.startsWith(`${normalizedBase}/`) ||
    url === normalizedBase ||
    url.startsWith(`${normalizedBase}/`)
  );
}

/**
 * Centralized logic to determine the appropriate auth token for a request.
 *
 * - FHIR requests: Use idToken (raw B2C token), fall back to customAccessToken
 * - Other requests: Use customAccessToken
 *
 * Used by both auth.interceptor and error.interceptor to maintain consistent token selection.
 *
 * @param url - Request URL
 * @param session - Current authentication session
 * @param fhirBaseUrl - Optional FHIR API base URL for FHIR request detection
 * @returns The appropriate token string, or null if session is inactive or no token available
 */
export function getAuthTokenForRequest(
  url: string,
  session: AuthenticationSession | null | undefined,
  fhirBaseUrl?: string | null
): string | null {
  if (!session || session.status !== AuthSessionStatus.SignedIn) {
    return null;
  }

  const isFhir = isFhirRequest(url, fhirBaseUrl);
  const token = isFhir ? (session.idToken ?? session.customAccessToken) : session.customAccessToken;

  return token || null;
}

/**
 * Global request interceptor for authentication and authorization.
 *
 * Responsibilities:
 *
 * - Inject Bearer token based on authentication state and request type
 *
 *   - FHIR API calls: inject idToken (raw B2C token), fallback to customAccessToken
 *   - All other calls: inject customAccessToken
 * - Filter by protected URLs when configured
 * - Fallback to AUTH_TOKEN_PROVIDER when session not active
 * - Stamp impersonation session header when active (on ALL requests)
 */
export const authInterceptor: HttpInterceptorFn = (request, next) => {
  const authenticationEnabled = inject(AUTHENTICATION_ENABLED);
  const authStateService = inject(AuthStateService, { optional: true });
  const fhirBaseUrl = inject(FHIR_API_BASE_URL, { optional: true });
  const impersonationService = inject(ImpersonationService);
  const provider = inject(AUTH_TOKEN_PROVIDER, { optional: true });

  let requestWithAuth = request;

  // Step 1: Handle Authorization token injection
  if (authenticationEnabled) {
    const protectedUrls = inject(AUTH_PROTECTED_RESOURCE_URLS, { optional: true }) ?? [];

    // Check if URL should be protected (empty list = all URLs protected)
    const isProtected = protectedUrls.length === 0 || protectedUrls.some((prefix) => request.url.startsWith(prefix));

    // Check if this is a FHIR request (FHIR requests always need auth token)
    const isFhir = isFhirRequest(request.url, fhirBaseUrl);

    // Inject token if URL is protected OR it's a FHIR request
    if (isProtected || isFhir) {
      // Try to get token from active session
      const session = authStateService?.getSession();
      const token = getAuthTokenForRequest(request.url, session, fhirBaseUrl);

      if (token) {
        requestWithAuth = request.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
      } else if (provider) {
        // Fallback to AUTH_TOKEN_PROVIDER if no session token available
        const tokenResult = provider.getToken();
        const token$ =
          tokenResult == null
            ? of(null)
            : typeof tokenResult === 'string'
              ? of(tokenResult)
              : typeof (tokenResult as Promise<unknown>).then === 'function'
                ? from(tokenResult as Promise<string | null | undefined>)
                : isObservable(tokenResult)
                  ? (tokenResult as import('rxjs').Observable<string | null | undefined>)
                  : of(null);

        return token$.pipe(
          switchMap((providerToken) => {
            let finalRequest = request;
            if (providerToken) {
              finalRequest = request.clone({ setHeaders: { Authorization: `Bearer ${providerToken}` } });
            }

            // Step 2: Add impersonation header if active (for provider fallback path)
            return next(addImpersonationHeaderIfActive(finalRequest, impersonationService, fhirBaseUrl));
          })
        );
      }
    }
  }

  // Step 2: Add impersonation header if active (for sync path)
  const finalRequest = addImpersonationHeaderIfActive(requestWithAuth, impersonationService, fhirBaseUrl);
  return next(finalRequest);
};

/**
 * Helper function to add impersonation header to request if impersonation is active. This is the single point where
 * impersonation header logic is applied.
 */
function addImpersonationHeaderIfActive(
  request: import('@angular/common/http').HttpRequest<unknown>,
  impersonationService: ImpersonationService,
  fhirBaseUrl: string | null | undefined
): import('@angular/common/http').HttpRequest<unknown> {
  if (isHeaderSkippedByPolicy(request.context, IMPERSONATION_SESSION_HEADER)) {
    return request;
  }

  // FHIR endpoints should never receive impersonation session header.
  if (isFhirRequest(request.url, fhirBaseUrl)) {
    return request;
  }

  const impersonationState = impersonationService.impersonationState();
  if (impersonationState.isActive && impersonationState.impersonationTokenId) {
    return request.clone({
      setHeaders: { [IMPERSONATION_SESSION_HEADER]: impersonationState.impersonationTokenId },
    });
  }

  return request;
}
